import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { type CreateBoardDraftRequest } from "../BoardDraftStore";
import { shouldReportBoardError } from "../boardFailures";

import { cacheCreatedBoard } from "./refreshBoardQueries";

/**
 * Start a board draft in this browser, resolving with it. Nothing is sent to
 * the platform until the person saves the draft (`useSaveBoardDraft`).
 */
export function useCreateBoardDraft() {
  const { boardService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateBoardDraftRequest) => {
      if (currentOrganization === null) {
        throw new Error("CurrentOrganization is not set");
      }
      return boardService.createBoardDraft(request, currentOrganization.org_id);
    },
    onSuccess: (draft) => {
      cacheCreatedBoard(queryClient, draft);
    },
    onError: (error) => {
      if (shouldReportBoardError(error)) {
        ErrorMonitoringService.captureError(error);
      }
    },
  });
}
